/**
 * Lightweight container sniffer — detects format and faststart without
 * loading the mediabunny demuxer. Runs instantly on a file prefix (2 MB is
 * enough for a faststart check, which is the whole point — if moov is at
 * the head, we find it without reading mdat).
 *
 * Returns a partial VideoProbeReport (container + file size only). Codec,
 * bitrate, and stream info require the full mediabunny probe.
 */

import type { ContainerFormat, ContainerInfo, VideoProbeReport } from './types';

const SNIFF_PREFIX_BYTES = 2 * 1024 * 1024;

export interface SniffResult {
  container: ContainerInfo;
  warnings: VideoProbeReport['warnings'];
  bailout: VideoProbeReport['bailout'];
}

export async function sniff(input: Blob | ArrayBuffer): Promise<SniffResult> {
  const fileSize = input instanceof Blob ? input.size : input.byteLength;
  const prefix = await readPrefix(input, Math.min(SNIFF_PREFIX_BYTES, fileSize));
  return sniffBuffer(prefix, fileSize);
}

export function sniffBuffer(prefix: Uint8Array, fileSize: number): SniffResult {
  if (prefix.byteLength < 8) {
    return {
      container: { format: 'unknown', brands: [], faststart: false, fragmented: false, fileSize },
      warnings: [{ code: 'too-small', message: 'Input is too small to identify a container.' }],
      bailout: 'parse-error',
    };
  }

  if (isEbml(prefix)) {
    return {
      container: { format: 'webm', brands: [], faststart: true, fragmented: false, fileSize },
      warnings: [{ code: 'ebml-detected', message: 'WebM/Matroska detected — full probe required.' }],
      bailout: null,
    };
  }

  if (!hasFtypAt(prefix, 0) && !hasFtypInFirst64KB(prefix)) {
    return {
      container: { format: 'unknown', brands: [], faststart: false, fragmented: false, fileSize },
      warnings: [{ code: 'no-ftyp', message: 'No MP4 ftyp box found near the start of the file.' }],
      bailout: 'unsupported-container',
    };
  }

  return parseTopLevelBoxes(prefix, fileSize);
}

function isEbml(b: Uint8Array): boolean {
  return b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3;
}

function hasFtypAt(b: Uint8Array, offset: number): boolean {
  if (b.byteLength < offset + 8) return false;
  return b[offset + 4] === 0x66 && b[offset + 5] === 0x74 && b[offset + 6] === 0x79 && b[offset + 7] === 0x70;
}

function hasFtypInFirst64KB(b: Uint8Array): boolean {
  if (b.byteLength < 8) return false;
  const limit = Math.min(b.byteLength - 8, 64 * 1024);
  for (let i = 0; i < limit; i++) {
    if (hasFtypAt(b, i)) return true;
  }
  return false;
}

function readUint32BE(b: Uint8Array, o: number): number {
  return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
}

function readUint64BE(b: Uint8Array, o: number): number {
  const hi = readUint32BE(b, o);
  const lo = readUint32BE(b, o + 4);
  return hi * 0x1_0000_0000 + lo;
}

function readFourcc(b: Uint8Array, o: number): string {
  return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);
}

function parseTopLevelBoxes(b: Uint8Array, fileSize: number): SniffResult {
  const warnings: VideoProbeReport['warnings'] = [];
  let brands: string[] = [];
  let containerFormat: ContainerFormat = 'mp4';
  let moovOffset: number | null = null;
  let mdatOffset: number | null = null;
  let sawMoof = false;
  let offset = 0;

  let ftypStart = 0;
  while (ftypStart + 8 < b.byteLength && !hasFtypAt(b, ftypStart)) ftypStart++;
  offset = ftypStart;

  while (offset + 8 <= b.byteLength) {
    const size32 = readUint32BE(b, offset);
    const type = readFourcc(b, offset + 4);
    let boxSize: number;
    let headerSize = 8;

    if (size32 === 1) {
      if (offset + 16 > b.byteLength) {
        warnings.push({ code: 'truncated-largesize', message: `Truncated largesize for box ${type} at offset ${offset}.` });
        return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: 'truncated' });
      }
      boxSize = readUint64BE(b, offset + 8);
      headerSize = 16;
    } else if (size32 === 0) {
      // Box extends to EOF. Guard against malformed input where offset already
      // exceeds the declared file size — without this, fileSize - offset would
      // be negative and the next-offset wrap would loop forever.
      if (offset >= fileSize) {
        warnings.push({ code: 'box-past-eof', message: `Size-0 box ${type} at offset ${offset} exceeds file size ${fileSize}.` });
        return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: 'truncated' });
      }
      boxSize = fileSize - offset;
    } else {
      boxSize = size32;
    }

    if (boxSize < headerSize) {
      warnings.push({ code: 'bad-box-size', message: `Box ${type} has invalid size ${boxSize} at offset ${offset}.` });
      return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: 'parse-error' });
    }

    if (type === 'ftyp') {
      const parsed = parseFtyp(b, offset + headerSize, offset + boxSize);
      brands = parsed.brands;
      containerFormat = detectFormatFromBrands(parsed.brands);
    } else if (type === 'moov' && moovOffset == null) {
      moovOffset = offset;
    } else if (type === 'mdat' && mdatOffset == null) {
      mdatOffset = offset;
    } else if (type === 'moof') {
      sawMoof = true;
    }

    if (moovOffset != null && mdatOffset != null) break;

    const next = offset + boxSize;
    if (next <= offset) {
      warnings.push({ code: 'non-progressing', message: `Box ${type} did not advance the offset.` });
      return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: 'parse-error' });
    }
    if (next > fileSize) {
      warnings.push({ code: 'box-exceeds-file', message: `Box ${type} extends past EOF.` });
      return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: 'truncated' });
    }

    offset = next;
    if (offset >= b.byteLength) break;
  }

  return finish({ brands, containerFormat, moovOffset, mdatOffset, sawMoof, fileSize, warnings, bailout: null });
}

function parseFtyp(b: Uint8Array, start: number, end: number): { brands: string[] } {
  const brands: string[] = [];
  if (end - start < 8) return { brands };
  brands.push(readFourcc(b, start).trim());
  let o = start + 8;
  while (o + 4 <= end) {
    brands.push(readFourcc(b, o).trim());
    o += 4;
  }
  return { brands };
}

function detectFormatFromBrands(brands: string[]): ContainerFormat {
  const joined = brands.join(' ').toLowerCase();
  if (joined.includes('qt')) return 'mov';
  if (joined.includes('m4v')) return 'm4v';
  return 'mp4';
}

interface FinishArgs {
  brands: string[];
  containerFormat: ContainerFormat;
  moovOffset: number | null;
  mdatOffset: number | null;
  sawMoof: boolean;
  fileSize: number;
  warnings: VideoProbeReport['warnings'];
  bailout: VideoProbeReport['bailout'];
}

function finish(args: FinishArgs): SniffResult {
  const faststart = args.moovOffset != null && args.mdatOffset != null
    ? args.moovOffset < args.mdatOffset
    : args.moovOffset != null && args.mdatOffset == null
      ? true
      : false;

  let bailout = args.bailout;
  const warnings = [...args.warnings];
  if (args.sawMoof) {
    bailout = 'fragmented-mp4';
    warnings.push({ code: 'fragmented-mp4', message: 'Fragmented MP4 detected (moof box present).' });
  }

  return {
    container: {
      format: args.containerFormat,
      brands: args.brands,
      faststart,
      fragmented: args.sawMoof,
      fileSize: args.fileSize,
    },
    warnings,
    bailout,
  };
}

async function readPrefix(input: Blob | ArrayBuffer, length: number): Promise<Uint8Array> {
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input, 0, Math.min(length, input.byteLength));
  }
  const slice = input.slice(0, length);
  const buf = await slice.arrayBuffer();
  return new Uint8Array(buf);
}
